/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import {IconButton} from '../Button';
import DropdownContent from './DropdownContent';
import styled from 'styled-components';

/**
 * Import custom types.
 */
import {DropdownButtonProps} from './DropdownButtonTypes';
import {defaultOnMouseDownHandler, useClickOutsideRef, useFocusOutsideRef} from '../common';
import {
  DropdownButtonTextArrow,
  DropdownButtonTextContent,
  DropdownButtonTextIcon,
  DropdownButtonTextStyles,
  DropdownButtonTextWrapper
} from './CommonStyling';
import {SystemIcons} from '../icons';

export const DropdownButtonContainer = styled.div`
  display: inline-block;
`;

const DropdownButton = React.forwardRef( ({
                          items,
                          icon,
                          disabled,
                          onClick,
                          itemsType = 'normal',
                          action,
                          actionLabel = '',
                          actionVariant,
                          actionIcon,
                          actionLoading,
                          size,
                          width,
                          alignLeft,
                          multiSelect = false,
                          scrollable = false,
                          pinTopItem = false,
                          maxHeight,
                          className,
                          value,
                          id,
                          dataTestId,
                          ...rest
                        }: DropdownButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  // Globally used variables within the view.
  const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);
  const [activeDescendant, setActiveDescendant] = React.useState<string>();
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [focused, setFocused] = React.useState<number | null>(null);
  const [keyboardNavigated, setKeyboardNavigated] = React.useState<boolean>(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const containerRef = useFocusOutsideRef(() => setDropdownOpen(false), [dropdownRef]);
  useClickOutsideRef(() => setDropdownOpen(false), [], containerRef);

  React.useEffect(() => {
    !dropdownOpen && setKeyboardNavigated(false)
  }, [dropdownOpen]);

  React.useEffect(() => {
    disabled && setDropdownOpen(false)
  }, [disabled]);

  React.useEffect(() => {
    setSelectedValues(value || []);
  }, [value]);

  const handleValueSelect = (values: string[]) => {
    // Do all required actions
    onClick(values);
  };

  /**
   * Renders Icon Button dropdown menu.
   * @returns HTML View for the Icon button dropdown menu.
   */
  const renderButton = () => {
    switch (rest.type) {
      case 'icon':
        return (
          <IconButton ref={ref}
                      variant="secondary"
                      shape="circular"
                      role="button"
                      tooltip={rest.tooltip}
                      id={id}
                      data-testid={dataTestId}
                      aria-controls={`${id}_dropdowncontent`}
                      aria-activedescendant={activeDescendant}
                      
                      action={(event: any) => {
                        setDropdownOpen(!dropdownOpen);
                        if (event?.detail !== 1) {
                          setKeyboardNavigated(true);
                        }
                      }}
                      disabled={disabled}>
            {icon}
          </IconButton>
        );
      case 'text':
        const label = rest.keepLabel || !selectedValues?.length
          ? rest.label
          : items.filter(a => selectedValues.includes(a.value)).map(a => a.displayLabel ?? a.value).join(', ');
        const cls = `${disabled && 'disabled'} ${dropdownOpen && 'expanded'} ${className}`;
        return <DropdownButtonTextWrapper tabIndex={!disabled ? 0 : -1}
                                          className={cls}
                                          role="button"
                                          id={id}
                                          data-testid={dataTestId}
                                          aria-controls={`${id}_dropdowncontent`}
                                          aria-activedescendant={activeDescendant}
                                          onMouseDown={defaultOnMouseDownHandler}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setDropdownOpen(!dropdownOpen);
                                              setKeyboardNavigated(true);
                                            }
                                          }}
                                          onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}>
          <DropdownButtonTextContent>
            {icon && <DropdownButtonTextIcon>{icon}</DropdownButtonTextIcon>}
            <DropdownButtonTextStyles>{label}</DropdownButtonTextStyles>
            <DropdownButtonTextArrow>
              {
                dropdownOpen
                  ? <SystemIcons.ArrowDropUp size="24px"/>
                  : <SystemIcons.ArrowDropDown size="24px"/>
              }
            </DropdownButtonTextArrow>
          </DropdownButtonTextContent>
        </DropdownButtonTextWrapper>
    }
  };

  return (
    <DropdownButtonContainer className={className} ref={containerRef}> 
      {renderButton()}
      <DropdownContent
        ref={dropdownRef}
        ariaRolesType="menu"
        containerRef={containerRef}
        customizationProps={{
          itemsType: itemsType,
          action: action ?? (() => {
          }),
          actionLabel: actionLabel,
          actionVariant: actionVariant,
          onValueUpdate: handleValueSelect,
          multiSelect: multiSelect,
          actionIcon: actionIcon,
          actionLoading: actionLoading,
          scrollable: scrollable,
          pinTopItem: pinTopItem,
          maxHeight: maxHeight,
          items: items
        }}
        onActiveDescendantChanged={(e) => setActiveDescendant(e)}
        focused={focused}
        setFocused={setFocused}
        size={size}
        width={width}
        alignLeft={alignLeft}
        isOpen={dropdownOpen}
        setIsOpen={setDropdownOpen}
        outline={keyboardNavigated}
        filter=""
        selectedValues={rest.type === 'icon' && itemsType === 'normal' ? [] : selectedValues}
        setSelectedValues={setSelectedValues}
        messageOnNoResults="No results"
        isButton={true}
        id={`${id}_dropdowncontent`}
      />
    </DropdownButtonContainer>
  );
});

export default DropdownButton;
